// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';

// components
import { formatNumber } from '@/utils/formatters';

import { useTheme } from '@emotion/react';
import { DateTime } from 'luxon';

// Function to group data by day and sum values
const groupDataByDay = (data) => {
  if (!data || data.length === 0) return [];
  const grouped = data.reduce((acc, { timestamp, value }) => {
    const day = DateTime.fromISO(timestamp).toFormat('yyyy-MM-dd'); // Group by day
    acc[day] = (acc[day] || 0) + value; // Sum values for the same day
    return acc;
  }, {});

  return Object.entries(grouped).map(([date, value]) => ({
    timestamp: date, // Keep the day as the timestamp
    value,
  }));
};

// Function to select 10 representative data points
const selectRepresentativePoints = (data, maxPoints = 20) => {
  if (!data || data.length === 0) return [];

  // Sort by timestamp (grouped by day already)
  const sortedData = [...data].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  );

  if (sortedData.length <= maxPoints) return sortedData; // If we have <=10 points, use al
  const result = [sortedData[0]]; // Always include first point
  const step = (sortedData.length - 1) / (maxPoints - 1); // Step size for even distribution

  for (let i = 1; i < maxPoints - 1; i++) {
    const index = Math.round(i * step);
    result.push(sortedData[index]);
  }

  result.push(sortedData[sortedData.length - 1]); // Always include last point
  return result;
};

const AreaGradient = ({ color, id }) => (
  <defs>
    <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
      <stop offset="100%" stopColor={color} stopOpacity={0} />
    </linearGradient>
  </defs>
);

const ConsumptionCard = ({ energyDataSummary, dataPoints, warningSlot }) => {
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  const { energyGroupId } = useParams({ strict: false });
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );
  const theme = useTheme();
  const { unit } = useEnergyGroupUnit();

  // Group data by day and then select representative points
  const groupedData = useMemo(() => groupDataByDay(dataPoints), [dataPoints]);
  const selectedData = useMemo(
    () => selectRepresentativePoints(groupedData),
    [groupedData],
  );

  const sparklineData = selectedData.map((entry) =>
    Number(entry.value.toFixed(0)),
  );
  const tooltipLabels = selectedData.map((entry) =>
    DateTime.fromISO(entry.timestamp)
      .setZone('Europe/Copenhagen')
      .toFormat('dd MMM yyyy'),
  );

  const gradientId = 'sparkline-gradient';

  const settings = {
    valueFormatter: (value) => `${formatNumber(value)} ${unit}`,
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {t('consumption_card.title')}
            <InfoTooltipIcon title={t('consumption_card.description')} />
          </Box>
        }
      />
      <CardContent>
        <Typography variant="h4" color="textPrimary" fontWeight="bold">
          {formatNumber(energyDataSummary?.TotalEnergyUsage || 0)} {unit}
        </Typography>
        <Box>{warningSlot}</Box>

        <Box>
          <svg width={0} height={0}>
            <defs>
              <linearGradient
                id="sparklineGradientRed"
                x1="0"
                x2="0"
                y1="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#52b202" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#52b202" stopOpacity={0} />
              </linearGradient>
            </defs>
          </svg>
          <SparkLineChart
            data={sparklineData}
            height={50}
            color={
              theme.palette[selectedEnergyGroup?.type?.toLowerCase()]?.main ||
              '#52b202'
            } // Green color for the line
            showTooltip
            showHighlight
            xAxis={{
              scaleType: 'band', // Required for tooltips to work properly
              data: tooltipLabels, // Provide formatted date labels
            }}
            area={true} // Enables the filled area below the line
            curve="monotoneX" // Smooths out the line curve
            {...settings}
            sx={{
              [`& .${areaElementClasses.root}`]: {
                fill: `url(#${gradientId})`,
              },
            }}
          >
            <AreaGradient
              color={
                theme.palette[selectedEnergyGroup?.type?.toLowerCase()]?.main ||
                '#52b202'
              }
              id={gradientId}
            />
          </SparkLineChart>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConsumptionCard;
